import React, { useState, useCallback, useEffect } from 'react';
import {
  ReactFlow,
  addEdge,
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
} from '@xyflow/react';
import { Modal, ModalHeader, ModalBody, Button, Form, FormGroup, Label, Input, Toast, ToastBody, ToastHeader, ModalFooter } from 'reactstrap';
import { useSelector, useDispatch } from 'react-redux';
import { getAIConversations, getK3Agents, getCommits, getAllCollections, getK3Tasks } from '../../actions';
import { getK3Flows } from '../../actions/k3flowAction';
import { saveAs } from 'file-saver';
import { k3docService } from '../../services';
import { flowToK3Doc, k3DocToFlow } from '../../utils/k3flow';
import IframeModal from '../../components/IframeModal';
import Select from 'react-select';
import ColorConfigItem from '../../components/ColorPicker/ColorConfigItem';
import ColorPickerModal from '../../components/ColorPicker/ColorPickerModal';
import EmojiPicker from '../../components/EmojiPicker/EmojiPicker';
import {
  IconChevronRight,
  IconChevronLeft,
} from '@tabler/icons-react';

// import '@xyflow/react/dist/style.css';
import './index.css';
import './styles.css';

// import {
//   nodes as initialNodes,
//   edges as initialEdges,
// } from './initial-elements';

import AnnotationNode from './nodes/AnnotationNode';
import ToolbarNode from './nodes/ToolbarNode';
import ResizerNode from './nodes/ResizerNode';
import CircleNode from './nodes/CircleNode';
import TextInputNode from './nodes/TextInputNode';
import ButtonEdge from './nodes/ButtonEdge';
// import ColorPicker from './nodes/ColorPicker';
import LinkNode from './nodes/LinkNode';
import RectangleNode from './nodes/RectangleNode';

const nodeTypes = {
  annotation: AnnotationNode,
  tools: ToolbarNode,
  resizer: ResizerNode,
  circle: CircleNode,
  textinput: TextInputNode,
  link: LinkNode,
  rectangle: RectangleNode,
};

const edgeTypes = {
  button: ButtonEdge,
};

const nodeClassName = (node) => node.type;

function getQueryParam(name) {
  const url = window.location.search;
  const params = new URLSearchParams(url);
  return params.get(name);
}

const FlowView = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showNodeModal, setShowNodeModal] = useState(false);
  const [selectedType, setSelectedType] = useState('conversation');
  const [selectedItem, setSelectedItem] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [k3Doc, setK3Doc] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingFlow, setEditingFlow] = useState({ title: '', friendlyVersion: '', description: '' });
  const [showIframeModal, setShowIframeModal] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');
  const [iframeTitle, setIframeTitle] = useState('');
  const [autoFillTitle, setAutoFillTitle] = useState(localStorage.getItem('autoFillTitle') === 'true');
  const [showEdgeModal, setShowEdgeModal] = useState(false);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [showToolbox, setShowToolbox] = useState(() => {
    const stored = localStorage.getItem('toolboxVisible');
    return stored === 'true';
  });
  const [defaultViewMode, setDefaultViewMode] = useState(() => {
    return localStorage.getItem('defaultViewMode') || 'modal';
  });
  const [config, setConfig] = useState({
      nodeStroke: localStorage.getItem('cfg_nodeStroke') || '#dddddd',
      nodeFill: localStorage.getItem('cfg_nodeFill') || '#ffffff',
      nodeWidth: Number(localStorage.getItem('cfg_nodeWidth')) || 120,
      nodeHeight: Number(localStorage.getItem('cfg_nodeHeight')) || 60,
      handleColor: localStorage.getItem('cfg_handleColor') || '#222222',
      handleDiameter: Number(localStorage.getItem('cfg_handleDiameter')) || 18,
      handleBorderColor: localStorage.getItem('cfg_handleBorderColor') || '#fff',
      badgeColor_conversation: localStorage.getItem('cfg_badgeColor_conversation') || '#007bff',
      badgeColor_agent: localStorage.getItem('cfg_badgeColor_agent') || '#28a745',
      badgeColor_commit: localStorage.getItem('cfg_badgeColor_commit') || '#ffc107',
      badgeColor_collection: localStorage.getItem('cfg_badgeColor_collection') || '#6f42c1',
      badgeColor_task: localStorage.getItem('cfg_badgeColor_task') || '#4CAF50',
      badgeColor_flow: localStorage.getItem('cfg_badgeColor_flow') || '#17a2b8',
      badgeColor_custom: localStorage.getItem('cfg_badgeColor_custom') || '#6c757d',
      edgeColor_default: localStorage.getItem('cfg_edgeColor_default') || '#555',
      edgeColor_straight: localStorage.getItem('cfg_edgeColor_straight') || '#555',
      edgeColor_step: localStorage.getItem('cfg_edgeColor_step') || '#555',
      edgeColor_smoothstep: localStorage.getItem('cfg_edgeColor_smoothstep') || '#555',
      snapToGrid: localStorage.getItem('cfg_snapToGrid') === 'true',
      gridSize: Number(localStorage.getItem('cfg_gridSize')) || 20,
  });
  const [showStrokeColorModal, setShowStrokeColorModal] = useState(false);
  const [showFillColorModal, setShowFillColorModal] = useState(false);
  const [showEdgeStrokeColorModal, setShowEdgeStrokeColorModal] = useState(false);
  const [headerCollapsed, setHeaderCollapsed] = useState(() => {
    const stored = localStorage.getItem('flowHeaderCollapsed');
    return stored === 'true';
  });
  const [showTextColorModal, setShowTextColorModal] = useState(false);
  const [showBadgeColorModal, setShowBadgeColorModal] = useState(false);
  const [showRectStrokeColorModal, setShowRectStrokeColorModal] = useState(false);
  const [showRectFillColorModal, setShowRectFillColorModal] = useState(false);

  // Redux selectors
  const aiConversations = useSelector(state => state.aiConversationReducer.aiConversations || []);
  const k3agents = useSelector(state => state.k3agentReducer.k3agents || []);
  const commits = useSelector(state => state.commitReducer.commits || []);
  const collections = useSelector(state => state.collectionReducer.collections || []);
  const k3tasks = useSelector(state => state.k3taskReducer.k3tasks || []);
  const flows = useSelector(state => state.k3flowReducer.flows || []);
  const dispatch = useDispatch();

  useEffect(() => {
    const mid = getQueryParam('mid');
    const id = getQueryParam('id');
    if (id) {
      k3docService.getK3Docs({ id }).then(res => {
          const docs = res.data.data.docs;
        if (docs && docs.length > 0) {
          const k3Doc = docs[0];
          setK3Doc(k3Doc);
          const flow = k3DocToFlow(k3Doc);
          setNodes(flow?.nodes || []);
          setEdges(flow?.edges || []);
        } else {
          setNodes(initialNodes);
          setEdges(initialEdges);
        }
      });
    } else if (mid) {
      k3docService.getK3Docs({ docMid: mid }).then(res => {
          const docs = res.data.data.docs;
          if (docs && docs.length > 0) {
            const k3Doc = docs[0];
          setK3Doc(k3Doc);
            const flow = k3DocToFlow(k3Doc);
          setNodes(flow?.nodes || []);
          setEdges(flow?.edges || []);
          } else {
          setNodes(initialNodes);
          setEdges(initialEdges);
          }
      });
    } else {
      setNodes(initialNodes);
      setEdges(initialEdges);
    }
    dispatch(getAIConversations());
    dispatch(getK3Agents());
    dispatch(getCommits());
    dispatch(getAllCollections());
    dispatch(getK3Tasks());
    dispatch(getK3Flows());
  }, [dispatch]);

  useEffect(() => {
    if (headerCollapsed) {
      document.body.classList.add('header-collapsed');
    } else {
      document.body.classList.remove('header-collapsed');
    }
  }, [headerCollapsed]);

  const onConnect = useCallback(
    (params) => {
      // Get latest edge style from localStorage
      let latestEdgeStyle = {};
      try {
        latestEdgeStyle = JSON.parse(localStorage.getItem('latestEdgeStyle')) || {};
      } catch (e) {
        console.error('Error parsing latestEdgeStyle:', e);
      }

      // Create new edge with latest style
      const newEdge = {
        ...params,
        style: {
          strokeWidth: latestEdgeStyle.strokeWidth || 2,
          stroke: latestEdgeStyle.stroke || '#555',
        },
        animated: latestEdgeStyle.animated || false,
        type: latestEdgeStyle.type || 'default',
        markerStart: latestEdgeStyle.markerStart ? { type: latestEdgeStyle.markerStart } : undefined,
        markerEnd: latestEdgeStyle.markerEnd ? { type: latestEdgeStyle.markerEnd } : undefined,
      };

      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges]
  );

  const onNodeDoubleClick = useCallback((event, node) => {
    setSelectedNode(node);
    setShowNodeModal(true);
    setSelectedType(node.data?.linkItemType || 'conversation');
    setSelectedItem(node.data?.linkItemId || '');
  }, []);

  const handleNodeModalClose = () => {
    setShowNodeModal(false);
    setSelectedNode(null);
  };

  // Dropdown options
  const typeOptions = [
    { value: 'conversation', label: 'Conversation' },
    { value: 'agent', label: 'Agent' },
    { value: 'commit', label: 'Commit' },
    { value: 'collection', label: 'Collection' },
    { value: 'task', label: 'Task' },
    { value: 'flow', label: 'Flow' },
    { value: 'custom', label: 'Custom' },
  ];

  let itemOptions = [];
  if (selectedType === 'conversation') {
    itemOptions = aiConversations.map(i => ({ value: i.conversation_id, label: i.title || i.conversation_id }));
  } else if (selectedType === 'agent') {
    itemOptions = k3agents.map(i => ({ value: i._id, label: i.name }));
  } else if (selectedType === 'commit') {
    itemOptions = commits.map(i => ({ value: i._id, label: i.title }));
  } else if (selectedType === 'collection') {
    itemOptions = collections.map(i => ({ value: i._id, label: i.name }));
  } else if (selectedType === 'task') {
    itemOptions = k3tasks.map(i => ({ value: i._id, label: i.name }));
  } else if (selectedType === 'flow') {
    itemOptions = flows.map(i => ({ value: i.doc_mid, label: i.title || i.doc_mid }));
  }

  const handleTypeChange = (e) => {
    setSelectedType(e.target.value);
    setSelectedItem('');
    if (selectedNode) {
      setSelectedNode({
        ...selectedNode,
      data: {
          ...selectedNode.data,
          linkItemType: e.target.value,
          linkItemId: '',
        link: '',
        },
      });
    }
  };

  const handleItemChange = (e) => {
    setSelectedItem(e.target.value);
    if (selectedNode) {
      let link = '';
      let label = '';
      let title = '';
      if (selectedType === 'conversation') {
        const found = aiConversations.find(i => i.conversation_id === e.target.value);
        if (found) {
          link = `/v1/k3pl-chat?conversationId=${found.conversation_id}&show_menu=false`;
          label = found.title || '';
          title = found.title || '';
        }
      } else if (selectedType === 'agent') {
        const found = k3agents.find(i => i._id === e.target.value);
        if (found) {
          link = `/v1/agent/view?id=${found._id}`;
          label = found.name || '';
          title = found.name || '';
        }
      } else if (selectedType === 'commit') {
        const found = commits.find(i => i._id === e.target.value);
        if (found) {
          link = `/v1/commit/${found._id}`;
          label = found.title || '';
          title = found.title || '';
        }
      } else if (selectedType === 'collection') {
        const found = collections.find(i => i._id === e.target.value);
        if (found) {
          link = `/v1/collection/${found._id}`;
          label = found.name || '';
          title = found.name || '';
        }
      } else if (selectedType === 'task') {
        const found = k3tasks.find(i => i._id === e.target.value);
        if (found) {
          link = `/v1/task/${found._id}`;
          label = found.name || '';
          title = found.name || '';
        }
      } else if (selectedType === 'flow') {
        const found = flows.find(i => i.doc_mid === e.target.value);
        if (found) {
          link = `/v1/flow/view?mid=${found.doc_mid}`;
          label = found.title || '';
          title = found.title || '';
        }
      }
      setSelectedNode({
        ...selectedNode,
        data: {
          ...selectedNode.data,
          linkItemId: e.target.value,
          linkItemType: selectedType,
          link,
          label: autoFillTitle ? label : selectedNode.data.label,
          title: autoFillTitle ? title : selectedNode.data.title,
        },
      });
    }
  };

  const handleAutoFillTitleChange = (e) => {
    const checked = e.target.checked;
    setAutoFillTitle(checked);
    localStorage.setItem('autoFillTitle', checked);
    if (checked && selectedItem && selectedNode) {
      let title = '';
      if (selectedType === 'conversation') {
        const found = aiConversations.find(i => i.conversation_id === selectedItem);
        if (found) title = found.title || '';
      } else if (selectedType === 'agent') {
        const found = k3agents.find(i => i._id === selectedItem);
        if (found) title = found.name || '';
      } else if (selectedType === 'commit') {
        const found = commits.find(i => i._id === selectedItem);
        if (found) title = found.title || '';
      } else if (selectedType === 'collection') {
        const found = collections.find(i => i._id === selectedItem);
        if (found) title = found.name || '';
      } else if (selectedType === 'task') {
        const found = k3tasks.find(i => i._id === selectedItem);
        if (found) title = found.name || '';
      }
      if (title) {
        setSelectedNode({
          ...selectedNode,
          data: {
            ...selectedNode.data,
            title,
            label: title,
          },
        });
      }
    }
  };

  const handleNodePropertyChange = (e) => {
    const { name, value } = e.target;
    if (name === 'viewMode') {
      setDefaultViewMode(value);
      localStorage.setItem('defaultViewMode', value);
    }
    setSelectedNode((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        [name]: value,
      },
    }));
  };

  const handleNodeSave = () => {
    // Nếu là rectangle, đồng bộ style ra ngoài node.style
    if (selectedNode?.type === 'rectangle') {
      const data = selectedNode.data || {};
      selectedNode.style = {
        border: `${data.strokeWidth || 2}px solid ${data.color || '#222'}`,
        background: data.fill || '#fff',
        zIndex: Number(data.zIndex) || -10,
        opacity: data.opacity !== undefined ? Number(data.opacity) : 1
      };
      // Lưu data rectangle vào localStorage theo flowMid, bao gồm cả width/height nếu có
      const flowMid = k3Doc?.doc_mid || 'default';
      let saveData = { ...selectedNode.data };
      if (selectedNode.style?.width) saveData.width = selectedNode.style.width;
      if (selectedNode.style?.height) saveData.height = selectedNode.style.height;
      localStorage.setItem('rectangleNodeDefaults_' + flowMid, JSON.stringify(saveData));
    }
    setNodes((nds) =>
      nds.map((node) =>
        node.id === selectedNode.id ? { ...selectedNode } : node
      )
    );
    setShowNodeModal(false);
    setSelectedNode(null);
  };

  const handleNodeDelete = () => {
    if (!selectedNode) return;
    setNodes((nds) => nds.filter((n) => n.id !== selectedNode.id));
    setEdges((eds) => eds.filter((e) => e.source !== selectedNode.id && e.target !== selectedNode.id));
    setShowNodeModal(false);
    setSelectedNode(null);
  };

  const showToastMessage = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 2000);
  };

  const handleSaveFlowToServer = async () => {
    const mid = k3Doc.doc_mid;
    const title = k3Doc.title;
    const friendlyVersion = editingFlow.friendlyVersion;
    const flow = { nodes, edges, title, flow_mid: mid, friendly_version: friendlyVersion };
    const docData = flowToK3Doc(flow);
    let payload = { ...docData };
      delete payload._id;
    const options = { generate_version: true };
    try {
      await k3docService.setK3Docs([payload], options);
      showToastMessage('Lưu flow lên server thành công!');
    } catch (err) {
      showToastMessage('Lỗi khi lưu flow lên server!', 'danger');
    }
  };

  const handleAddNode = () => {
    const newNode = {
      id: `node-${Date.now()}`,
      type: 'link',
      position: { x: 100 + Math.random() * 200, y: 100 + Math.random() * 200 },
      data: {
        title: 'Node mới',
        label: 'Node mới',
        viewMode: defaultViewMode,
        link: '',
        content: '',
        color: '#dddddd',
        fill: '#ffffff',
        onView: handleNodeView,
      },
    };
    setNodes(nds => [...nds, newNode]);
  };

  const handleNodeView = (node) => {
    console.log('node?.data?.viewMode', node?.data?.viewMode);
    console.log('node?.data?.link', node?.data?.link);
    
    if (node?.data?.link && node?.data?.viewMode === 'iframe') {
      setIframeUrl(node.data.link);
      setIframeTitle(node.data.title || '');
      setShowIframeModal(true);
    } else if (node?.data?.link) {
      let link = node.data.link;
      if (!/^https?:\/\//i.test(link)) {
        if (!link.startsWith('/')) link = '/' + link;
        link = window.location.origin + link;
      }
      
      if (node?.data?.viewMode === 'direct') {
        window.location.href = link;
      } else {
        window.open(link, '_blank');
      }
    }
  };

  const renderRectangleNodeModal = () => {
    if (!selectedNode) return null;
    const nodeData = selectedNode.data || {};
    return (
      <Modal isOpen={showNodeModal} toggle={handleNodeModalClose} size="md" className="modal-dialog-centered" style={{maxWidth: 500}}>
        <ModalHeader toggle={handleNodeModalClose}>Edit Rectangle Node</ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup className="mb-3">
              <Label>Label</Label>
              <Input
                type="text"
                name="label"
                value={nodeData.label || ''}
                onChange={handleNodePropertyChange}
              />
            </FormGroup>
            <div className="row mb-3">
              <div className="col-md-6 d-flex flex-column">
                <Label>Stroke Color</Label>
                <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                  <span
                    onClick={() => setShowRectStrokeColorModal(true)}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 6,
                      border: '2px solid #bbb',
                      background: nodeData.color || '#222',
                      cursor: 'pointer',
                      display: 'inline-block',
                      boxShadow: '0 1px 4px #0001',
                    }}
                    title={nodeData.color || '#222'}
                  />
                  <span style={{fontFamily: 'monospace', fontSize: 14}}>{nodeData.color || '#222'}</span>
                </div>
                <ColorPickerModal
                  isOpen={showRectStrokeColorModal}
                  toggle={() => setShowRectStrokeColorModal(false)}
                  value={nodeData.color || '#222'}
                  onChange={color => {
                    handleNodePropertyChange({ target: { name: 'color', value: color } });
                    setShowRectStrokeColorModal(false);
                  }}
                />
              </div>
              <div className="col-md-6 d-flex flex-column">
                <Label>Fill Color</Label>
                <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                  <span
                    onClick={() => setShowRectFillColorModal(true)}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 6,
                      border: '2px solid #bbb',
                      background: nodeData.fill || '#fff',
                      cursor: 'pointer',
                      display: 'inline-block',
                      boxShadow: '0 1px 4px #0001',
                    }}
                    title={nodeData.fill || '#fff'}
                  />
                  <span style={{fontFamily: 'monospace', fontSize: 14}}>{nodeData.fill || '#fff'}</span>
                </div>
                <ColorPickerModal
                  isOpen={showRectFillColorModal}
                  toggle={() => setShowRectFillColorModal(false)}
                  value={nodeData.fill || '#fff'}
                  onChange={color => {
                    handleNodePropertyChange({ target: { name: 'fill', value: color } });
                    setShowRectFillColorModal(false);
                  }}
                />
              </div>
            </div>
            {/* Stroke width, z-index, opacity cùng 1 dòng */}
            <div className="row mb-3">
              <div className="col-md-4">
                <Label>Stroke Width</Label>
                <Input
                  type="number"
                  name="strokeWidth"
                  min={1}
                  max={12}
                  value={nodeData.strokeWidth || 2}
                  onChange={handleNodePropertyChange}
                />
              </div>
              <div className="col-md-4">
                <Label>Z-Index</Label>
                <Input
                  type="number"
                  name="zIndex"
                  min={-100}
                  max={100}
                  value={nodeData.zIndex || -1}
                  onChange={handleNodePropertyChange}
                />
              </div>
              <div className="col-md-4">
                <Label>Opacity</Label>
                <Input
                  type="number"
                  name="opacity"
                  min={0}
                  max={1}
                  step={0.05}
                  value={nodeData.opacity ?? 1}
                  onChange={handleNodePropertyChange}
                />
              </div>
            </div>
            <div className="d-flex gap-2 justify-content-end mt-3">
              <Button color="danger" onClick={handleNodeDelete}>Delete</Button>
              <Button color="primary" onClick={handleNodeSave}>Save</Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    );
  };

  const renderNodeModal = () => {
    if (!selectedNode) return null;
    if (selectedNode.type === 'rectangle') return renderRectangleNodeModal();
    const nodeData = selectedNode.data || {};
    const nodeStyle = nodeData.style || {};
    const isCustomShape = selectedNode.type === 'customShape';

    return (
      <Modal isOpen={showNodeModal} toggle={handleNodeModalClose} size="lg" className="modal-dialog-centered" style={{maxWidth: 700}}>
        <ModalHeader toggle={handleNodeModalClose} className="position-relative" style={{alignItems: 'center'}}>
          <span>Edit Node</span>
        </ModalHeader>
        <ModalBody>
          <Form>
            {/* Title & Auto Fill Title */}
            <FormGroup className="mb-3">
              <Label>Title</Label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Input
                  type="text"
                  name="title"
                  value={nodeData.title || ''}
                  onChange={handleNodePropertyChange}
                  style={{ flex: 1 }}
                />
                <FormGroup check style={{ marginBottom: 0, marginLeft: 8, minWidth: 140, display: 'flex', alignItems: 'center' }}>
                  <Input
                    type="checkbox"
                    checked={!!autoFillTitle}
                    onChange={handleAutoFillTitleChange}
                    id="auto-fill-title-checkbox"
                    style={{ marginRight: 6 }}
                  />
                  <Label check htmlFor="auto-fill-title-checkbox" style={{ marginBottom: 0, fontWeight: 400, fontSize: 14, cursor: 'pointer' }}>
                    Auto Fill Title
                  </Label>
                </FormGroup>
              </div>
            </FormGroup>
            <FormGroup className="mb-3">
              <Label>Label</Label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Input
                  type="text"
                  name="label"
                  value={nodeData.label || ''}
                  onChange={handleNodePropertyChange}
                  style={{ flex: 1 }}
                />
                {selectedNode?.type === 'link' && (
                  <EmojiPicker
                    value={selectedNode.data?.emoji || ''}
                    onChange={emoji => setSelectedNode(prev => ({
                      ...prev,
                      data: { ...prev.data, emoji }
                    }))}
                  />
                )}
              </div>
            </FormGroup>
            {/* Color Pickers */}
            <FormGroup className="mb-3">
              <div className="row">
                <div className="col-md-3 d-flex flex-column h-100">
                  <Label>Stroke Color</Label>
                  <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                    <span
                      onClick={() => setShowStrokeColorModal(true)}
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 6,
                        border: '2px solid #bbb',
                        background: nodeData.color || '#dddddd',
                        cursor: 'pointer',
                        display: 'inline-block',
                        boxShadow: '0 1px 4px #0001',
                      }}
                      title={nodeData.color || '#dddddd'}
                    />
                    <span style={{fontFamily: 'monospace', fontSize: 14}}>{nodeData.color || '#dddddd'}</span>
                  </div>
                  <ColorPickerModal
                    isOpen={showStrokeColorModal}
                    toggle={() => setShowStrokeColorModal(false)}
                    value={nodeData.color || '#dddddd'}
                    onChange={color => {
                      handleNodePropertyChange({ target: { name: 'color', value: color } });
                      setShowStrokeColorModal(false);
                    }}
                  />
                </div>
                <div className="col-md-3 d-flex flex-column h-100">
                  <Label>Fill Color</Label>
                  <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                    <span
                      onClick={() => setShowFillColorModal(true)}
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 6,
                        border: '2px solid #bbb',
                        background: nodeData.fill || '#ffffff',
                        cursor: 'pointer',
                        display: 'inline-block',
                        boxShadow: '0 1px 4px #0001',
                      }}
                      title={nodeData.fill || '#ffffff'}
                    />
                    <span style={{fontFamily: 'monospace', fontSize: 14}}>{nodeData.fill || '#ffffff'}</span>
                  </div>
                  <ColorPickerModal
                    isOpen={showFillColorModal}
                    toggle={() => setShowFillColorModal(false)}
                    value={nodeData.fill || '#ffffff'}
                    onChange={color => {
                      handleNodePropertyChange({ target: { name: 'fill', value: color } });
                      setShowFillColorModal(false);
                    }}
                  />
                </div>
                <div className="col-md-3 d-flex flex-column h-100">
                  <Label>Text Color</Label>
                  <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                    <span
                      onClick={() => setShowTextColorModal(true)}
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 6,
                        border: '2px solid #bbb',
                        background: nodeData.textColor || '#222222',
                        cursor: 'pointer',
                        display: 'inline-block',
                        boxShadow: '0 1px 4px #0001',
                      }}
                      title={nodeData.textColor || '#222222'}
                    />
                    <span style={{fontFamily: 'monospace', fontSize: 14}}>{nodeData.textColor || '#222222'}</span>
                  </div>
                  <ColorPickerModal
                    isOpen={showTextColorModal}
                    toggle={() => setShowTextColorModal(false)}
                    value={nodeData.textColor || '#222222'}
                    onChange={color => {
                      handleNodePropertyChange({ target: { name: 'textColor', value: color } });
                      setShowTextColorModal(false);
                    }}
                  />
                </div>
                {selectedNode?.type === 'link' && (
                  <div className="col-md-3 d-flex flex-column h-100">
                    <Label>Badge Color</Label>
                    <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                      <span
                        onClick={() => setShowBadgeColorModal(true)}
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 6,
                          border: '2px solid #bbb',
                          background: nodeData.customBadgeColor || '#eee',
                          cursor: 'pointer',
                          display: 'inline-block',
                          boxShadow: '0 1px 4px #0001',
                        }}
                        title={nodeData.customBadgeColor || 'Default'}
                      >
                        {!nodeData.customBadgeColor && (
                          <span style={{color:'#888',fontSize:15,display:'block',textAlign:'center'}}>x</span>
                        )}
                      </span>
                      <span style={{fontFamily: 'monospace', fontSize: 14}}>{nodeData.customBadgeColor || 'None'}</span>
                    </div>
                    <ColorPickerModal
                      isOpen={showBadgeColorModal}
                      toggle={() => setShowBadgeColorModal(false)}
                      value={nodeData.customBadgeColor || ''}
                      onChange={color => {
                        handleNodePropertyChange({ target: { name: 'customBadgeColor', value: color } });
                        setShowBadgeColorModal(false);
                      }}
                      label="Badge Color"
                    />
                  </div>
                )}
              </div>
            </FormGroup>
            {/* Node Link Block */}
            <div className="p-3 rounded border bg-light mb-3 mt-4">
              <div className="row">
                <div className="col-md-6">
                  <FormGroup>
                    <Label>Type</Label>
                    <Input type="select" value={selectedType} onChange={handleTypeChange}>
                      {typeOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Input>
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <Label>Select Item</Label>
                    <Select
                      options={itemOptions}
                      value={itemOptions.find(option => String(option.value) === String(selectedItem)) || null}
                      onChange={option => handleItemChange({ target: { value: option ? option.value : '' } })}
                      isSearchable
                      placeholder="Select ..."
                      className="react-select-container"
                      classNamePrefix="react-select"
                      isDisabled={selectedType === 'custom'}
                      styles={{
                        control: (base) => ({
                          ...base,
                          width: '100%',
                          fontSize: '0.95rem',
                          borderRadius: '0.25rem',
                          background: selectedType === 'custom' ? '#f3f3f3' : base.background,
                          pointerEvents: selectedType === 'custom' ? 'none' : 'auto',
                          opacity: selectedType === 'custom' ? 0.7 : 1,
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          width: '100%',
                          padding: '0 6px'
                        }),
                        input: (base) => ({
                          ...base,
                          margin: '0px',
                          width: '100%'
                        }),
                        indicatorsContainer: (base) => ({
                          ...base
                        })
                      }}
                    />
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <Label>View Mode</Label>
                    <Input
                      type="select"
                      name="viewMode"
                      value={nodeData.viewMode || defaultViewMode}
                      onChange={handleNodePropertyChange}
                    >
                      <option value="modal">Modal</option>
                      <option value="tab">Tab</option>
                      <option value="iframe">Iframe</option>
                      <option value="direct">Direct Link</option>
                    </Input>
                  </FormGroup>
                </div>
                <div className="col-md-6">
                  <FormGroup>
                    <Label>Link</Label>
                    <Input
                      type="text"
                      name="link"
                      value={nodeData.link || ''}
                      readOnly={selectedType !== 'custom'}
                      style={selectedType === 'custom' ? {background: '#fff'} : {background: '#f3f3f3'}}
                      onChange={handleNodePropertyChange}
                    />
                  </FormGroup>
                </div>
              </div>
            </div>
            {/* Metadata */}
            <FormGroup className="mb-3">
              <Label>Metadata</Label>
              <Input
                type="textarea"
                name="content"
                value={nodeData.content || ''}
                onChange={handleNodePropertyChange}
                rows={5}
              />
            </FormGroup>
            <div className="d-flex gap-2 justify-content-end mt-4 mb-2">
              <Button color="danger" onClick={handleNodeDelete}>Delete</Button>
              <Button color="primary" onClick={handleNodeSave}>Save</Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    );
  };

  const handleConfigChange = (key, value) => {
    setConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const saveConfig = () => {
    localStorage.setItem('cfg_nodeStroke', config.nodeStroke);
    localStorage.setItem('cfg_nodeFill', config.nodeFill);
    localStorage.setItem('cfg_nodeWidth', config.nodeWidth);
    localStorage.setItem('cfg_nodeHeight', config.nodeHeight);
    localStorage.setItem('cfg_handleColor', config.handleColor);
    localStorage.setItem('cfg_handleDiameter', config.handleDiameter);
    localStorage.setItem('cfg_handleBorderColor', config.handleBorderColor);
    localStorage.setItem('cfg_badgeColor_conversation', config.badgeColor_conversation);
    localStorage.setItem('cfg_badgeColor_agent', config.badgeColor_agent);
    localStorage.setItem('cfg_badgeColor_commit', config.badgeColor_commit);
    localStorage.setItem('cfg_badgeColor_collection', config.badgeColor_collection);
    localStorage.setItem('cfg_badgeColor_task', config.badgeColor_task);
    localStorage.setItem('cfg_badgeColor_flow', config.badgeColor_flow);
    localStorage.setItem('cfg_badgeColor_custom', config.badgeColor_custom);
    localStorage.setItem('cfg_edgeColor_default', config.edgeColor_default);
    localStorage.setItem('cfg_edgeColor_straight', config.edgeColor_straight);
    localStorage.setItem('cfg_edgeColor_step', config.edgeColor_step);
    localStorage.setItem('cfg_edgeColor_smoothstep', config.edgeColor_smoothstep);
    localStorage.setItem('cfg_snapToGrid', config.snapToGrid);
    localStorage.setItem('cfg_gridSize', config.gridSize);
    setShowConfigModal(false);
  };

  const renderConfigModal = () => (
    <Modal isOpen={showConfigModal} toggle={() => setShowConfigModal(false)} size="xxl" className="modal-dialog-centered" style={{minWidth: 900, maxWidth: 1200, margin: '0 auto'}}>
      <ModalHeader toggle={() => setShowConfigModal(false)} style={{fontWeight: 700, fontSize: 22, background: '#f8fafc', borderBottom: '1.5px solid #e0e6ed'}}>Cấu hình Flow</ModalHeader>
        <ModalBody style={{background: '#f6f8fa', borderRadius: 16}}>
          <Form>
          {/* LinkNode Config */}
          <div style={{marginBottom: 28, background: '#fff', borderRadius: 14, boxShadow: '0 2px 12px #0001', padding: 24, border: '1.5px solid #e0e6ed'}}>
            <div style={{fontWeight: 600, fontSize: 18, marginBottom: 14, color: '#1a202c'}}>LinkNode</div>
            <div className="row" style={{marginBottom: 18}}>
              <div className="col-md-3 col-sm-6 mb-3"><ColorConfigItem label="Stroke" value={config.nodeStroke} onChange={color => handleConfigChange('nodeStroke', color)} /></div>
              <div className="col-md-3 col-sm-6 mb-3"><ColorConfigItem label="Fill" value={config.nodeFill} onChange={color => handleConfigChange('nodeFill', color)} /></div>
              <div className="col-md-3 col-sm-6 mb-3"><ColorConfigItem label="Handle" value={config.handleColor} onChange={color => handleConfigChange('handleColor', color)} /></div>
              <div className="col-md-3 col-sm-6 mb-3"><ColorConfigItem label="Handle Border" value={config.handleBorderColor} onChange={color => handleConfigChange('handleBorderColor', color)} /></div>
            </div>
            <div style={{fontWeight: 600, fontSize: 18, margin: '10px 0 8px 0', color: '#3b4252'}}>LinkNode Badge</div>
            <div className="row" style={{marginBottom: 4}}>
              <div className="col-md-3 col-sm-6 mb-3"><ColorConfigItem label="Conversation" value={config.badgeColor_conversation} onChange={color => handleConfigChange('badgeColor_conversation', color)} /></div>
              <div className="col-md-3 col-sm-6 mb-3"><ColorConfigItem label="Agent" value={config.badgeColor_agent} onChange={color => handleConfigChange('badgeColor_agent', color)} /></div>
              <div className="col-md-3 col-sm-6 mb-3"><ColorConfigItem label="Commit" value={config.badgeColor_commit} onChange={color => handleConfigChange('badgeColor_commit', color)} /></div>
              <div className="col-md-3 col-sm-6 mb-3"><ColorConfigItem label="Collection" value={config.badgeColor_collection} onChange={color => handleConfigChange('badgeColor_collection', color)} /></div>
              <div className="col-md-3 col-sm-6 mb-3"><ColorConfigItem label="Task" value={config.badgeColor_task} onChange={color => handleConfigChange('badgeColor_task', color)} /></div>
              <div className="col-md-3 col-sm-6 mb-3"><ColorConfigItem label="Flow" value={config.badgeColor_flow} onChange={color => handleConfigChange('badgeColor_flow', color)} /></div>
              <div className="col-md-3 col-sm-6 mb-3"><ColorConfigItem label="Custom" value={config.badgeColor_custom} onChange={color => handleConfigChange('badgeColor_custom', color)} /></div>
            </div>
          </div>
          {/* Link Type (Edge) Config */}
          <div style={{marginBottom: 28, background: '#fff', borderRadius: 14, boxShadow: '0 2px 12px #0001', padding: 24, border: '1.5px solid #e0e6ed'}}>
            <div style={{fontWeight: 600, fontSize: 18, marginBottom: 14, color: '#1a202c'}}>Edge Type</div>
            <div className="row">
              <div className="col-md-3 col-sm-6 mb-3"><ColorConfigItem label="Default" value={config.edgeColor_default} onChange={color => handleConfigChange('edgeColor_default', color)} /></div>
              <div className="col-md-3 col-sm-6 mb-3"><ColorConfigItem label="Straight" value={config.edgeColor_straight} onChange={color => handleConfigChange('edgeColor_straight', color)} /></div>
              <div className="col-md-3 col-sm-6 mb-3"><ColorConfigItem label="Step" value={config.edgeColor_step} onChange={color => handleConfigChange('edgeColor_step', color)} /></div>
              <div className="col-md-3 col-sm-6 mb-3"><ColorConfigItem label="Smoothstep" value={config.edgeColor_smoothstep} onChange={color => handleConfigChange('edgeColor_smoothstep', color)} /></div>
            </div>
          </div>
          {/* Node size, handle size, snap */}
          <div className="d-flex flex-wrap mt-4" style={{gap: 32, background: '#fff', borderRadius: 14, boxShadow: '0 2px 12px #0001', padding: 24, border: '1.5px solid #e0e6ed'}}>
            <FormGroup className="mb-3" style={{minWidth: 220}}>
              <Label style={{fontWeight: 500}}>Kích thước node (px)</Label>
              <div className="d-flex" style={{gap: 12}}>
                <Input
                  type="number"
                  min={60}
                  max={400}
                  value={config.nodeWidth}
                  onChange={e => handleConfigChange('nodeWidth', Number(e.target.value))}
                  style={{width: 90, borderRadius: 8, border: '1.5px solid #e0e6ed'}}
                  placeholder="Width"
                />
                <Input
                  type="number"
                  min={30}
                  max={200}
                  value={config.nodeHeight}
                  onChange={e => handleConfigChange('nodeHeight', Number(e.target.value))}
                  style={{width: 90, borderRadius: 8, border: '1.5px solid #e0e6ed'}}
                  placeholder="Height"
                />
              </div>
            </FormGroup>
            <FormGroup className="mb-3" style={{minWidth: 220}}>
              <Label style={{fontWeight: 500}}>Đường kính handle (px)</Label>
              <Input
                type="number"
                min={8}
                max={40}
                value={config.handleDiameter}
                onChange={e => handleConfigChange('handleDiameter', Number(e.target.value))}
                style={{width: 90, borderRadius: 8, border: '1.5px solid #e0e6ed'}}
              />
            </FormGroup>
            <FormGroup className="mb-3" style={{minWidth: 220}}>
              <Label style={{fontWeight: 500}}>Kích thước grid (px)</Label>
              <Input
                type="number"
                min={5}
                max={100}
                value={config.gridSize}
                onChange={e => handleConfigChange('gridSize', Number(e.target.value))}
                style={{width: 90, borderRadius: 8, border: '1.5px solid #e0e6ed'}}
              />
            </FormGroup>
            <FormGroup className="mb-3 d-flex align-items-center" style={{minWidth: 220, marginTop: 28}}>
              <Input
                type="checkbox"
                id="snapToGrid"
                checked={config.snapToGrid}
                onChange={e => handleConfigChange('snapToGrid', e.target.checked)}
                style={{marginRight: 8, width: 18, height: 18, accentColor: '#2196f3'}}
              />
              <Label check htmlFor="snapToGrid" style={{fontWeight: 500, marginBottom: 0}}>Snap to Grid</Label>
            </FormGroup>
          </div>
        </Form>
        </ModalBody>
        <ModalFooter style={{background: '#f8fafc', borderTop: '1.5px solid #e0e6ed', borderRadius: '0 0 14px 14px'}}>
        <Button color="secondary" onClick={() => setShowConfigModal(false)} style={{borderRadius: 8, fontWeight: 500}}>Hủy</Button>
        <Button color="primary" onClick={saveConfig} style={{borderRadius: 8, fontWeight: 500}}>Lưu cấu hình</Button>
        </ModalFooter>
    </Modal>
  );

  const renderEditModal = () => (
    <Modal isOpen={showEditModal} toggle={() => setShowEditModal(false)}>
      <ModalHeader toggle={() => setShowEditModal(false)}>Chỉnh sửa thông tin Flow</ModalHeader>
        <ModalBody>
          <Form>
          <FormGroup className="mb-3">
            <Label>Tiêu đề</Label>
            <Input
                type="text"
                name="title"
                value={editingFlow.title}
              onChange={e => setEditingFlow(f => ({ ...f, title: e.target.value }))}
            />
          </FormGroup>
          <FormGroup className="mb-3">
            <Label>Friendly Version</Label>
            <Input
                type="text"
                name="friendlyVersion"
                value={editingFlow.friendlyVersion}
              onChange={e => setEditingFlow(f => ({ ...f, friendlyVersion: e.target.value }))}
                placeholder="Ví dụ: v1.0.0"
              />
          </FormGroup>
          <FormGroup className="mb-3">
            <Label>Mô tả</Label>
            <Input
              type="textarea"
                name="description"
                value={editingFlow.description}
              onChange={e => setEditingFlow(f => ({ ...f, description: e.target.value }))}
                rows={3}
              />
          </FormGroup>
          </Form>
        </ModalBody>
      <div className="d-flex justify-content-end gap-2 p-3">
        <Button color="secondary" onClick={() => setShowEditModal(false)}>Hủy</Button>
        <Button color="primary" onClick={() => {
          setK3Doc(doc => doc ? { ...doc, title: editingFlow.title, friendly_version: editingFlow.friendlyVersion, description: editingFlow.description } : doc);
          setShowEditModal(false);
        }}>Lưu thay đổi</Button>
      </div>
    </Modal>
  );

  const handleEditFlow = () => {
    setEditingFlow({
      title: k3Doc?.title || '',
      friendlyVersion: k3Doc?.friendly_version || '',
      description: k3Doc?.description || ''
    });
    setShowEditModal(true);
  };

  const nodesWithOnView = nodes.map(n => n.type === 'link' ? { ...n, data: { ...n.data, onView: handleNodeView } } : n);

  console.log({ AnnotationNode, ToolbarNode, ResizerNode, CircleNode, TextInputNode, ButtonEdge });

  // Khi click vào edge
  const onEdgeClick = (event, edge) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedEdge(edge);
    setShowEdgeModal(true);
  };

  const handleEdgeModalClose = () => {
    setShowEdgeModal(false);
    setSelectedEdge(null);
  };

  const handleEdgePropertyChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSelectedEdge(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleEdgeStyleChange = (e) => {
    const { name, value } = e.target;
    setSelectedEdge(prev => ({
      ...prev,
      style: { ...prev.style, [name]: value }
    }));
  };

  const handleEdgeSave = () => {
    // Save edge style to localStorage before updating edges
    const styleToSave = {
      strokeWidth: selectedEdge.style?.strokeWidth || 2,
      stroke: selectedEdge.style?.stroke || '#555',
      animated: selectedEdge.animated || false,
      type: selectedEdge.type || 'default',
      markerStart: typeof selectedEdge.markerStart === 'object' ? selectedEdge.markerStart.type : selectedEdge.markerStart,
      markerEnd: typeof selectedEdge.markerEnd === 'object' ? selectedEdge.markerEnd.type : selectedEdge.markerEnd,
    };
    localStorage.setItem('latestEdgeStyle', JSON.stringify(styleToSave));

    setEdges(eds => eds.map(e => e.id === selectedEdge.id ? { ...selectedEdge } : e));
    setShowEdgeModal(false);
    setSelectedEdge(null);
  };

  const handleEdgeDelete = () => {
    setEdges(eds => eds.filter(e => e.id !== selectedEdge.id));
    setShowEdgeModal(false);
    setSelectedEdge(null);
  };

  const renderEdgeModal = () => {
    if (!selectedEdge) return null;
    const style = selectedEdge.style || {};
    // ColorPicker recent colors
    let recentEdgeColors = [];
    try {
      recentEdgeColors = JSON.parse(localStorage.getItem('recentEdgeColors') || '[]');
    } catch {}
    if (!Array.isArray(recentEdgeColors)) recentEdgeColors = [];
    const addRecentEdgeColor = (color) => {
      let arr = recentEdgeColors.filter(c => c !== color);
      arr.unshift(color);
      if (arr.length > 8) arr = arr.slice(0, 8);
      localStorage.setItem('recentEdgeColors', JSON.stringify(arr));
    };
    // Marker type value helpers
    const markerStartVal = typeof selectedEdge.markerStart === 'object' && selectedEdge.markerStart?.type ? selectedEdge.markerStart.type : (selectedEdge.markerStart || '');
    const markerEndVal = typeof selectedEdge.markerEnd === 'object' && selectedEdge.markerEnd?.type ? selectedEdge.markerEnd.type : (selectedEdge.markerEnd || '');
    return (
      <Modal isOpen={showEdgeModal} toggle={handleEdgeModalClose} size="md" className="modal-dialog-centered">
        <ModalHeader toggle={handleEdgeModalClose}>
          Edit Edge
        </ModalHeader>
        <ModalBody>
          <Form>
            {/* Row 1: Label | Type */}
            <div className="d-flex align-items-center mb-2" style={{gap: 12}}>
              <FormGroup style={{flex: 1}}>
                <Label>Label</Label>
                <Input type="text" name="label" value={selectedEdge.label || ''} onChange={handleEdgePropertyChange} />
              </FormGroup>
              <FormGroup style={{flex: 1}}>
                <Label>Type</Label>
                <Input type="select" name="type" value={selectedEdge.type || 'default'} onChange={handleEdgePropertyChange}>
                  <option value="default">Default</option>
                  <option value="straight">Straight</option>
                  <option value="step">Step</option>
                  <option value="smoothstep">Smooth Step</option>
                  <option value="button">Button</option>
                </Input>
              </FormGroup>
            </div>
            {/* Row 2: Stroke Width | Stroke Color | Animated */}
            <div className="d-flex align-items-center mb-2" style={{gap: 12}}>
              <FormGroup style={{flex: 2}}>
                <Label>Stroke Width</Label>
                <Input type="number" name="strokeWidth" value={style.strokeWidth || 2} onChange={handleEdgeStyleChange} min={1} max={10} step={1} />
              </FormGroup>
              <FormGroup style={{flex: 1}}>
                <Label>Stroke Color</Label>
                <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                  <span
                    onClick={() => setShowEdgeStrokeColorModal(true)}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 6,
                      border: '2px solid #bbb',
                      background: style.stroke || '#555',
                      cursor: 'pointer',
                      display: 'inline-block',
                      boxShadow: '0 1px 4px #0001',
                    }}
                    title={style.stroke || '#555'}
                  />
                  <span style={{fontFamily: 'monospace', fontSize: 14}}>{style.stroke || '#555'}</span>
                </div>
                <ColorPickerModal
                  isOpen={showEdgeStrokeColorModal}
                  toggle={() => setShowEdgeStrokeColorModal(false)}
                  value={style.stroke || '#555'}
                  onChange={color => {
                    handleEdgeStyleChange({ target: { name: 'stroke', value: color } });
                    setShowEdgeStrokeColorModal(false);
                  }}
                />
              </FormGroup>
              <FormGroup check style={{display: 'flex', alignItems: 'center', height: '38px', marginTop: 24}}>
                <Input
                  type="checkbox"
                  name="animated"
                  checked={!!selectedEdge.animated}
                  onChange={e => setSelectedEdge(prev => ({ ...prev, animated: e.target.checked }))}
                  style={{marginRight: 6, alignSelf: 'center'}}
                  id="edge-animated-checkbox"
                />
                <Label htmlFor="edge-animated-checkbox" style={{marginBottom: -5, marginLeft: 5, cursor: 'pointer', fontWeight: 500, alignSelf: 'center'}}>
                  Animated
                </Label>
              </FormGroup>
            </div>
            {/* Row 3: Marker Start | Marker End */}
            <FormGroup className="mb-2 d-flex align-items-center" style={{gap: 12}}>
              <div style={{flex: 1}}>
                <Label>Marker Start</Label>
                <Input type="select" name="markerStart" value={markerStartVal} onChange={handleEdgePropertyChange}>
                  <option value="">None</option>
                  <option value="arrow">Arrow</option>
                  <option value="arrowclosed">ArrowClosed</option>
                </Input>
            </div>
              <div style={{flex: 1}}>
                <Label>Marker End</Label>
                <Input type="select" name="markerEnd" value={markerEndVal} onChange={handleEdgePropertyChange}>
                  <option value="">None</option>
                  <option value="arrow">Arrow</option>
                  <option value="arrowclosed">ArrowClosed</option>
                </Input>
                </div>
            </FormGroup>
            {/* Row cuối: Source/Target ID */}
            {/* <div style={{fontSize: 13, color: '#888', marginTop: 10}}>
              {selectedEdge?.source && <span><b>Source:</b> {selectedEdge.source}</span>}
              {selectedEdge?.source && selectedEdge?.target && <span> | </span>}
              {selectedEdge?.target && <span><b>Target:</b> {selectedEdge.target}</span>}
            </div> */}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handleEdgeDelete}>Delete</Button>
          <Button color="secondary" onClick={handleEdgeModalClose}>Cancel</Button>
          <Button color="primary" onClick={handleEdgeSave}>Save</Button>
        </ModalFooter>
      </Modal>
    );
  };

  // Sửa hàm insert node từ toolbox
  const handleInsertNodeFromToolbox = (type) => {
    // Lấy flowMid
    const flowMid = k3Doc?.doc_mid || 'default';
    // Lấy vị trí node gần nhất từ localStorage
    let lastPos = null;
    try {
      lastPos = JSON.parse(localStorage.getItem('lastNodePos_' + flowMid));
    } catch {}
    // Nếu có vị trí thì cộng offset, không thì lấy giữa màn hình
    const baseX = lastPos?.x ?? (window.innerWidth / 2 - 200);
    const baseY = lastPos?.y ?? (window.innerHeight / 2 - 100);
    // Đặt mặc định cho rectangle node
    let nodeData = {
      label: '',
      color: '#dddddd',
      fill: '#ffffff',
      viewMode: 'modal',
      link: '',
      content: '',
      onView: handleNodeView,
    };
    if (type === 'rectangle') {
      // Lấy data rectangle mặc định từ localStorage nếu có
      let rectDefaults = null;
      try {
        rectDefaults = JSON.parse(localStorage.getItem('rectangleNodeDefaults_' + flowMid));
      } catch {}
      nodeData = {
        ...nodeData,
        zIndex: -10,
        width: 200,
        height: 200,
        ...rectDefaults, // Ưu tiên thuộc tính đã lưu
      };
    }
    const newNode = {
      id: `node-${type}-${Date.now()}`,
      type,
      position: { x: baseX + 40, y: baseY + 40 },
      data: nodeData,
      ...(type === 'rectangle' ? {
        style: {
          border: `${nodeData.strokeWidth || 2}px solid ${nodeData.color || '#222'}`,
          background: nodeData.fill || '#fff',
          zIndex: Number(nodeData.zIndex) || -10,
          opacity: nodeData.opacity !== undefined ? Number(nodeData.opacity) : 1,
          borderRadius: 8,
        }
      } : {})
    };
    setNodes(nds => [...nds, newNode]);
  };

  // Sửa onNodesChange để lưu vị trí node vừa kéo
  const onNodesChangeWithPos = useCallback((changes) => {
    onNodesChange(changes); // gọi hàm gốc
    // Lưu vị trí node vừa kéo
    const flowMid = k3Doc?.doc_mid || 'default';
    const moved = changes.find(c => c.type === 'position' && c.position);
    if (moved) {
      localStorage.setItem('lastNodePos_' + flowMid, JSON.stringify(moved.position));
    }
  }, [onNodesChange, k3Doc]);

  // Sửa onNodeDoubleClick để lưu vị trí node
  const onNodeDoubleClickWithPos = useCallback((event, node) => {
    setSelectedNode(node);
    setShowNodeModal(true);
    setSelectedType(node.data?.linkItemType || 'conversation');
    setSelectedItem(node.data?.linkItemId || '');
    // Lưu vị trí node
    const flowMid = k3Doc?.doc_mid || 'default';
    if (node?.position) {
      localStorage.setItem('lastNodePos_' + flowMid, JSON.stringify(node.position));
    }
  }, [k3Doc]);

  // Danh sách node types cho toolbox
  const toolboxNodeTypes = [
    { type: 'annotation', label: 'Annotation' },
    { type: 'tools', label: 'Tools' },
    { type: 'resizer', label: 'Resizer' },
    { type: 'rectangle', label: 'Rectangle' },
    { type: 'circle', label: 'Circle' },
    { type: 'textinput', label: 'Text Input' },
    { type: 'link', label: 'Link Node' },
  ];

  // Hàm mở/đóng toolbox và lưu localStorage
  const handleShowToolbox = (val) => {
    setShowToolbox(val);
    localStorage.setItem('toolboxVisible', val);
    };

  const handleToggleHeader = () => {
    setHeaderCollapsed(prev => {
      localStorage.setItem('flowHeaderCollapsed', !prev);
      return !prev;
    });
  };

    return (
    <div style={{ width: '100%', height: '100%' }}>
      {/* Header với chức năng thu gọn */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: headerCollapsed ? '6px 10px' : '16px', borderBottom: '2px solid #eee', marginBottom: 8, minHeight: headerCollapsed ? 36 : 64, transition: 'all 0.2s' }}>
        {/* Bên trái: Tên flow */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: headerCollapsed ? 15 : 22, fontWeight: 600, color: '#222', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>{k3Doc?.title || '[No Title]'}</span>
          <button
            onClick={handleToggleHeader}
            className="header-toggle-btn"
            title={headerCollapsed ? 'Hiện header' : 'Thu gọn header'}
          >
            <span className="header-toggle-icon">
              {headerCollapsed ? (
                <IconChevronRight size={18} color="#888" />
              ) : (
                <IconChevronLeft size={18} color="#888" />
              )}
            </span>
          </button>
        </div>
        {/* Bên phải: Nhóm icon insert node nhanh + nhóm nút chức năng */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          {/* Nhóm icon insert node nhanh */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <button onClick={() => handleInsertNodeFromToolbox('annotation')} title="Thêm Annotation Node" style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', padding: 2 }}>
              📝
            </button>
            <button onClick={() => handleInsertNodeFromToolbox('tools')} title="Thêm Tools Node" style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', padding: 2 }}>
              🛠️
            </button>
            <button onClick={() => handleInsertNodeFromToolbox('resizer')} title="Thêm Resizer Node" style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', padding: 2 }}>
              ↔️
            </button>
            <button onClick={() => handleInsertNodeFromToolbox('rectangle')} title="Thêm Rectangle Node" style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', padding: 2 }}>
              🟦
            </button>
            <button onClick={() => handleInsertNodeFromToolbox('circle')} title="Thêm Circle Node" style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', padding: 2 }}>
              ⚪
            </button>
            <button onClick={() => handleInsertNodeFromToolbox('textinput')} title="Thêm Text Input Node" style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', padding: 2 }}>
              🔤
            </button>
            <button onClick={() => handleInsertNodeFromToolbox('link')} title="Thêm Link Node" style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', padding: 2 }}>
              🔗
            </button>
          </div>
          {/* Divider */}
          <div style={{ width: 1, height: 28, background: '#e0e0e0', margin: '0 7px' }} />
          {/* Nhóm nút chức năng */}
          <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
            {headerCollapsed ? (
              <>
                {/* Load data */}
                <button
                  onClick={() => {
                    dispatch(getAIConversations());
                    dispatch(getK3Agents());
                    dispatch(getCommits());
                    dispatch(getAllCollections());
                    dispatch(getK3Tasks());
                    dispatch(getK3Flows());
                    showToastMessage('Đã tải lại dữ liệu thành công!');
                  }}
                  title="Tải lại dữ liệu"
                  style={{ background: 'none', border: 'none', padding: 4, color: '#17a2b8', fontSize: 18, cursor: 'pointer' }}
                >
                  🔄
                </button>
                {/* Config */}
                <button
                  onClick={() => setShowConfigModal(true)}
                  title="Cấu hình"
                  style={{ background: 'none', border: 'none', padding: 4, color: '#6c757d', fontSize: 18, cursor: 'pointer' }}
                >
                  ⚙️
                </button>
                {/* Edit */}
                <button
                  onClick={handleEditFlow}
                  title="Chỉnh sửa thông tin"
                  style={{ background: 'none', border: 'none', padding: 4, color: '#007bff', fontSize: 18, cursor: 'pointer' }}
                >
                  ✏️
                </button>
                {/* Save Flow */}
                <button
                  onClick={handleSaveFlowToServer}
                  title="Lưu Flow"
                  style={{ background: 'none', border: 'none', padding: 4, color: '#4CAF50', fontSize: 18, cursor: 'pointer' }}
                >
                  💾
                </button>
              </>
            ) : (
              <>
                <Button 
                  color="info" 
                  onClick={() => {
                    dispatch(getAIConversations());
                    dispatch(getK3Agents());
                    dispatch(getCommits());
                    dispatch(getAllCollections());
                    dispatch(getK3Tasks());
                    dispatch(getK3Flows());
                    showToastMessage('Đã tải lại dữ liệu thành công!');
                  }}
                  title="Tải lại dữ liệu"
                >
                  <span style={{fontSize: 16, marginRight: 6}}>🔄</span>
                  Tải lại dữ liệu
                </Button>
                <Button color="secondary" onClick={() => setShowConfigModal(true)} title="Cấu hình">
                  <span style={{fontSize: 16, marginRight: 6}}>⚙️</span>
                  Cấu hình
                </Button>
                <Button color="primary" onClick={handleEditFlow} title="Chỉnh sửa thông tin">
                  <span style={{fontSize: 16, marginRight: 6}}>✏️</span>
                  Chỉnh sửa
                </Button>
                <Button color="success" onClick={handleSaveFlowToServer} title="Lưu Flow">
                  <span style={{fontSize: 16, marginRight: 6}}>💾</span>
                  Lưu Flow
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      {renderConfigModal()}
      {renderEditModal()}
      {/* Toast notification */}
      <div style={{ position: 'fixed', top: 24, right: 24, zIndex: 9999 }}>
        <Toast isOpen={showToast} color={toastType} fade={true} style={{ minWidth: 240 }}>
          <ToastHeader toggle={() => setShowToast(false)} icon={toastType === 'success' ? 'success' : 'danger'}>
            {toastType === 'success' ? 'Success' : 'Error'}
          </ToastHeader>
          <ToastBody>{toastMessage}</ToastBody>
        </Toast>
      </div>
          <ReactFlow
        nodes={nodesWithOnView}
            edges={edges}
        onNodesChange={onNodesChangeWithPos}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeDoubleClick={onNodeDoubleClickWithPos}
        onEdgeClick={onEdgeClick}
        fitView
        attributionPosition="top-right"
            nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
            nodesDraggable={true}
        nodesConnectable={true}
            elementsSelectable={true}
        selectNodesOnDrag={false}
        panOnDrag={true}
        zoomOnScroll={true}
        preventScrolling={true}
        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        snapToGrid={config.snapToGrid}
        snapGrid={[config.gridSize, config.gridSize]}
      >
        <MiniMap zoomable pannable nodeClassName={nodeClassName} />
            <Controls />
            <Background />
          </ReactFlow>
      {renderNodeModal()}
      {renderEdgeModal()}
      {/* Iframe modal */}
      <IframeModal show={showIframeModal} onHide={() => setShowIframeModal(false)} url={iframeUrl} title={iframeTitle} />
      {/* Toolbox panel bên phải */}
      <div style={{ position: 'fixed', top: 56, right: 0, height: '100%', zIndex: 2000 }}>
        {!showToolbox && (
          <button
            style={{ position: 'absolute', top: 80, right: 0, zIndex: 2001, borderRadius: '8px 0 0 8px', border: '1px solid #ddd', background: '#fff', padding: '8px 12px', boxShadow: '0 2px 8px #0001', cursor: 'pointer' }}
            onClick={() => handleShowToolbox(true)}
            title="Open Toolbox"
          >
            {'>'}
          </button>
        )}
        {showToolbox && (
          <div style={{ width: 220, height: 490, background: '#fafbfc', borderLeft: '1.5px solid #e0e0e0', boxShadow: '-2px 0 8px #0001', padding: 0, position: 'relative', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #eee', fontWeight: 600, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              Toolbox
              <button onClick={() => handleShowToolbox(false)} style={{ border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: '#888', marginLeft: 8 }} title="Close">×</button>
        </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 8px' }}>
              {toolboxNodeTypes.map(n => (
                <div key={n.type} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, cursor: 'pointer', border: '1.5px solid #e0e0e0', borderRadius: 8, background: '#fff', padding: '10px 12px', boxShadow: '0 1px 4px #0001', transition: 'background 0.15s', fontWeight: 500 }}
                  onClick={() => handleInsertNodeFromToolbox(n.type)}
                  title={`Insert ${n.label} node`}
                >
                  <span style={{ width: 28, height: 28, borderRadius: 6, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, color: '#888', border: '1px solid #eee' }}>
                    {n.type === 'annotation' && '📝'}
                    {n.type === 'tools' && '🛠️'}
                    {n.type === 'resizer' && '↔️'}
                    {n.type === 'rectangle' && '🟦'}
                    {n.type === 'circle' && '⚪'}
                    {n.type === 'textinput' && '🔤'}
                    {n.type === 'link' && '🔗'}
                  </span>
                  <span>{n.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      </div>
    );
};

export default FlowView; 